import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Prop, Watch } from '@Ravnur-Inc/decorators';
import { Vue, Options } from 'vue-class-component';

import MediaListSnippet from '../media-list-snippet/media-list-snippet';
import MediaPickerModal from '@/components/media/media-picker-modal/media-picker-modal';
import PopupButton from '@/components/popup-button/popup-button';

import BaseRepository from '@/repositories/base-repository';

import { MediaQueryParams } from '@/repositories/media-repository';
import { toCheckable, getChecked } from '@/transformers/checkable';
import { Media, Media$Privacy, OrderableCheckableMedia } from '@/types/Media';
import ReorderingList from '@/components/reordering-list/reordering-list';
import { toOrderable } from '@/transformers/orderable';

import './media-sublist.scss';

const CN = 'media-sublist';

type Props = {
  origin: Media;
  repository: BaseRepository<Media>;
  editText: string;
  showTypes?: boolean;
  queryParams?: Partial<MediaQueryParams>;
  privacy?: Media$Privacy[];
};

type Emits = {
  onSublistChanged: () => void;
};

@Options({
  inject: {
    refreshPlayerSources: 'refreshPlayerSources',
  },
})
export default class MediaSublist extends Vue {
  declare $props: $Types<Props, Emits>;
  declare refreshPlayerSources: (() => void) | undefined;

  @Prop({ type: Object, required: true })
  public origin!: Media;
  @Prop({ type: Object, required: true })
  public repository!: BaseRepository<Media>;
  @Prop({ type: String, required: true })
  public editText!: string;
  @Prop({ type: Boolean, default: true })
  public showTypes!: boolean;
  @Prop({ type: Object, default: () => ({}) })
  public queryParams!: Partial<MediaQueryParams>;
  @Prop({ default: [] }) privacy: Media$Privacy[];
  @Prop({ default: false }) excludeExisted: boolean;

  private loading = false;
  private checkableList: Array<OrderableCheckableMedia> = [];
  private columns = 3;

  get mediaId() {
    return this.origin.id;
  }

  private get checked(): Media[] {
    return getChecked(this.checkableList);
  }

  get items(): Media[] {
    return this.checkableList.map((w) => w.entity);
  }

  set items(list: Media[]) {
    this.checkableList = toOrderable(toCheckable(list));
    this.refreshPlayerSources?.();
  }

  @Watch('mediaId', { immediate: true })
  protected async load() {
    this.loading = true;

    try {
      const list = await this.repository.load();
      this.checkableList = toOrderable(toCheckable(list));
    } catch (e) {
      this.$processException(e);
    }

    this.loading = false;
  }

  render() {
    const excluded = this.excludeExisted ? this.items : [];

    return (
      <div class={CN}>
        <div class={`${CN}__header`}>
          <PopupButton
            class={`${CN}__modal-btn`}
            color="primary"
            icon="add"
            mode="standard"
            text={this.editText}
          >
            <MediaPickerModal
              excluded={[this.origin, ...excluded]}
              privacy={this.privacy}
              process={this.append}
              queryParams={this.queryParams}
              showTypes={this.showTypes}
              value={[]}
            />
          </PopupButton>
        </div>

        <ReorderingList
          localId
          v-slots={{
            item: ({ entity }: { entity: OrderableCheckableMedia }) => this.renderCard(entity),
          }}
          vModel={[this.checkableList, 'list']}
          onUpdate:list={this.handleDragEnd}
        />
        {this.loading ? <spinner /> : null}
      </div>
    );
  }

  protected async handleDragEnd(items: OrderableCheckableMedia[]) {
    await this.fetch(items.map(({ entity }) => entity));
  }

  private async append(list: Media[]) {
    return this.fetch([...list, ...this.items]);
  }

  private async fetch(items: Media[]) {
    try {
      await this.repository.saveMany(items);
      this.items = items;
    } catch (e) {
      this.$processException(e);
    }
  }

  private renderCard(wrapper: OrderableCheckableMedia) {
    const cn = [`${CN}__card`, `${CN}__card--1x${this.columns}`];
    const slots = {
      actions: () => (
        <r-button
          class={`${CN}__delete`}
          icon="trash"
          mode="frameless"
          onclick={() => this.removeItem(wrapper)}
        />
      ),
    };
    return (
      <MediaListSnippet key={wrapper.entity.id} class={cn} media={wrapper.entity} v-slots={slots} />
    );
  }

  async removeItem(wrapper: OrderableCheckableMedia) {
    const finalItems = this.items.filter((w) => w.id !== wrapper.entity.id);

    this.loading = true;
    try {
      await this.fetch(finalItems);
    } catch (e) {
      this.$processException(e);
    } finally {
      this.loading = false;
    }
  }
}
